/**
 * Invitation Accept Page
 *
 * Public page (requiresAuth: false) for accepting or declining a
 * company-member invitation by token. Fetches the invitation preview so the
 * invitee sees WHO invited them into WHAT before deciding — an anonymous
 * page acting on a blind token was indistinguishable from phishing. When no
 * account exists for the invited email, accepting collects a name and
 * password and the backend creates the account (partner-guest protocol).
 */

import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, X } from '@lognai/synapse-design-system-v2/icons';
import {
  Button,
  Input,
  Label,
  Logo,
  Textarea,
  useTheme,
  useToast,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@lognai/synapse-design-system-v2';
import { PasswordRequirements } from '@lognai/plugin-components';
import { firstPasswordProblem } from '@lognai/plugin-sdk';
import {
  useInvitationPreview,
  useAcceptInvitation,
  useDeclineInvitation,
} from '../hooks/use-invitation-hooks';
import { usePasswordPolicy } from '../hooks/use-password-policy';

/**
 * The same shell every other auth screen uses: a centred, max-width Card with
 * the wordmark above the title.
 *
 * This page used to centre itself in the viewport with a `Building2` glyph in a
 * tinted circle and a `text-2xl font-bold` heading — a motif and a type scale
 * that appear nowhere else in the auth family, on the one screen a brand-new
 * user sees first (QA ledger AUTH-015).
 */
const Shell: React.FC<{ title: string; description?: React.ReactNode; children?: React.ReactNode }> = ({
  title,
  description,
  children,
}) => {
  const { theme } = useTheme();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-col items-center space-y-4 pb-6 text-center">
          <Logo variant={theme === 'light' ? 'dark' : 'white'} className="h-12 w-auto" />
          <CardTitle className="text-xl font-normal">{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        {children && <CardContent>{children}</CardContent>}
      </Card>
    </div>
  );
};

export const InvitationAcceptPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const policy = usePasswordPolicy();

  const token = searchParams.get('token');

  const previewQuery = useInvitationPreview(token);
  const preview = previewQuery.data?.invitation;
  const needsAccount = preview ? !preview.accountExists : false;

  const acceptMutation = useAcceptInvitation({
    onSuccess: () => {
      toast({
        title: 'Welcome!',
        description: preview?.restoresAccount
          ? 'Your account has been restored — log in with your new password.'
          : needsAccount
            ? 'Your account is ready — log in to get started.'
            : 'You have successfully joined the company.',
      });
      // A brand-new account has no session yet; existing users may or may
      // not be logged in. /login handles both (and forwards a live session).
      navigate('/login');
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const declineMutation = useDeclineInvitation({
    onSuccess: () => {
      toast({
        title: 'Invitation Declined',
        description: 'The invitation has been declined.',
      });
      navigate('/');
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleAccept = () => {
    if (!token) return;
    if (needsAccount) {
      // The whole policy, not just its length rule — this checked length alone
      // and left the rest to the server's rejection (QA ledger AUTH-008).
      const problem = firstPasswordProblem(policy, password);
      if (problem) {
        toast({ title: 'Choose a stronger password', description: problem, variant: 'destructive' });
        return;
      }
      if (password !== confirmPassword) {
        toast({
          title: 'Passwords do not match',
          description: 'Re-enter the same password in both fields.',
          variant: 'destructive',
        });
        return;
      }
    }
    acceptMutation.mutate({
      token,
      password: needsAccount ? password : undefined,
      fullName: needsAccount && fullName ? fullName : undefined,
    });
  };

  const handleDecline = () => {
    if (!token) return;
    declineMutation.mutate({ token, reason: declineReason || undefined });
  };

  if (!token) {
    return (
      <Shell
        title="Invalid invitation link"
        description="No invitation token was provided. Please use the full link from your email."
      >
        <Button className="w-full" onClick={() => navigate('/')}>
          Go Home
        </Button>
      </Shell>
    );
  }

  if (previewQuery.isLoading) {
    return (
      <Shell title="Loading invitation…" />
    );
  }

  // Unknown token, expired/consumed invitation, or a wrong-kind token: say so
  // up front instead of after a click.
  if (previewQuery.isError || !preview || preview.status !== 'PENDING') {
    const detail =
      preview && preview.status !== 'PENDING'
        ? `This invitation is ${preview.status.toLowerCase()} and can no longer be used.`
        : 'This invitation link is invalid or has expired. Ask the sender for a new one.';
    return (
      <Shell title="Invitation unavailable" description={detail}>
        <Button className="w-full" onClick={() => navigate('/')}>
          Go Home
        </Button>
      </Shell>
    );
  }

  return (
    <Shell
      title={`Join ${preview.companyName ?? 'the company'}`}
      description={
        <>
          {preview.email} has been invited
          {preview.roleName ? ` as ${preview.roleName}` : ''}
          {preview.jobTitle ? ` — ${preview.jobTitle}` : ''}
          {preview.message && (
            <span className="mt-3 block italic">&ldquo;{preview.message}&rdquo;</span>
          )}
        </>
      }
    >
      <>
          {showDeclineForm ? (
            <div className="space-y-4">
              <h2 className="font-semibold">Decline Invitation</h2>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to decline this invitation? You can optionally provide a
                reason.
              </p>
              <Textarea
                placeholder="Reason for declining (optional)"
                value={declineReason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDeclineReason(e.target.value)}
                rows={3}
              />
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDeclineForm(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={handleDecline}
                  disabled={declineMutation.isPending}
                >
                  {declineMutation.isPending ? 'Declining...' : 'Confirm Decline'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {needsAccount ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    {preview.restoresAccount
                      ? 'Welcome back — choose a new password to restore your previous account.'
                      : 'You don\u2019t have an account yet — set one up to join.'}
                  </p>
                  {!preview.restoresAccount && (
                  <div className="space-y-2">
                    <Label htmlFor="invite-full-name">Full name</Label>
                    <Input
                      id="invite-full-name"
                      value={fullName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFullName(e.target.value)}
                      placeholder="Your name"
                      autoComplete="name"
                    />
                  </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="invite-password">Password</Label>
                    <Input
                      id="invite-password"
                      type="password"
                      value={password}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                      placeholder="Choose a password"
                      autoComplete="new-password"
                    />
                    <PasswordRequirements policy={policy} password={password} className="pt-1" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invite-confirm">Confirm password</Label>
                    <Input
                      id="invite-confirm"
                      type="password"
                      value={confirmPassword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                </>
              ) : (
                <p className="text-center text-muted-foreground">
                  Click accept to join and start collaborating with your team.
                </p>
              )}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDeclineForm(true)}
                >
                  <X className="h-4 w-4 mr-2" />
                  Decline
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleAccept}
                  disabled={acceptMutation.isPending}
                >
                  <Check className="h-4 w-4 mr-2" />
                  {acceptMutation.isPending ? 'Accepting...' : 'Accept'}
                </Button>
              </div>
            </div>
          )}
      </>
    </Shell>
  );
};

export default InvitationAcceptPage;
